import { createContext, useContext } from 'react';

/** Where overlay portals (modals, popovers, menus, selects, tooltips, toasts) are mounted. */
export type PortalContainer = HTMLElement | ShadowRoot | React.RefObject<HTMLElement | ShadowRoot | null> | null;

const PortalContainerContext = createContext<PortalContainer | undefined>(undefined);

/**
 * Renders every shared/ui overlay below it into `container` instead of `document.body`.
 * Use it around a scaled stage (02-constructor §8) so popups inherit the stage transform:
 *
 *   <div ref={stageRef} style={{ transform: `scale(${s})` }}>
 *     <PortalContainerProvider container={stageRef}>…</PortalContainerProvider>
 *   </div>
 */
export function PortalContainerProvider({ container, children }: { container: PortalContainer; children: React.ReactNode }) {
  return <PortalContainerContext value={container}>{children}</PortalContainerContext>;
}

/** The explicit `container` prop wins, then the nearest provider, then Base UI's default (`document.body`). */
export function usePortalContainer(container?: PortalContainer): PortalContainer | undefined {
  const fromContext = useContext(PortalContainerContext);
  return container === undefined ? fromContext : container;
}
